import { Injectable, inject } from '@angular/core';

import { Observable, filter, switchMap, take, takeUntil, takeWhile, tap } from 'rxjs';

import { AccountsFacade } from '@personal-area/global/accounts/services';
import { AuthService } from '@personal-area/global/authorization/services';
import { LoadingState } from '@personal-area/global/state';
import { CountryChangerService } from '@personal-area/global/country-change';
import { UserFacade, UserService } from '@personal-area/global/users/services';
import { CardVerificationFacade, SummaryVerificationFacade } from '@personal-area/global/verification';
import { SocketConnectionState, WebsocketFallbackService } from '@personal-area/global/websocket';

@Injectable()
export class GlobalWebsocketFallbackService extends WebsocketFallbackService {
  private _userFacade = inject(UserFacade);

  constructor(
    private _authService: AuthService,
    private _accountsFacade: AccountsFacade,
    private _userService: UserService,
    private _cardVerificationFacade: CardVerificationFacade,
    private _countryChangerService: CountryChangerService,
    private _summaryVerificationFacade: SummaryVerificationFacade,
  ) {
    super();
  }

  public reloadAccounts(): void {
    this._init()?.pipe(
      switchMap(() => this._accountsFacade.getAccountsLoadingState$),
      filter((state) => state === LoadingState.loaded),
      take(1),
    )
      .subscribe(() => this._accountsFacade.getAccounts());
  }

  public reloadUser(): void {
    this._init()?.subscribe(() => this._userService.loadUser());
  }

  public reloadStatus(): void {
    this._init()
      ?.pipe(
        tap(() => {
          this._summaryVerificationFacade.getSummary();
          this._cardVerificationFacade.getCard();
          this._countryChangerService.refreshStatus();
        }),
        switchMap(() => this._countryChangerService.getStatus()),
      )
      .subscribe(() =>
        this._countryChangerService.userCountryStatusChanged.next(),
      );
  }

  protected _init(): Observable<SocketConnectionState> {
    if (!this._authService.isLoggedIn) {
      return;
    }

    return this.socketConnectionState
      .pipe(
        takeUntil(this._authService.onLogout),
        takeWhile(state => state !== SocketConnectionState.connected),
        filter(state => state === SocketConnectionState.failed),
        take(1)
      )
  }
}
